import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, FileText, Grid, Settings, Users, Plus, Edit, Trash2, Eye, Search,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Loader2, Download, Check, X, Image as ImageIcon,
  CreditCard, AlertCircle, Save, Percent, Building, LogOut, MessageSquare,
  CheckCircle, XCircle, RefreshCw, Mail, Phone, Shield, Upload, GripVertical, Star,
  Send, ClipboardList, DollarSign, Clock, User, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

// Admin Customers Management with Verification Status
export const AdminCustomers = () => {
  const { user, getAuthHeaders, loading: userLoading } = useApp();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (userLoading) return; // Wait for user to load
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userLoading, navigate]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() });
      setCustomers(res.data.customers);
    } catch (e) { toast.error('Failed to load customers'); }
    setLoading(false);
  };

  const handleVerifyEmail = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/verify-email`, {}, { headers: getAuthHeaders() });
      toast.success('Email verified manually');
      fetchCustomers();
    } catch (e) { toast.error('Failed to verify email'); }
    setActionLoading(null);
  };

  const handleResendOTP = async (userId) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-otp`, {}, { headers: getAuthHeaders() });
      toast.success('OTP sent to customer');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send OTP'); }
    setActionLoading(null);
  };

  const handleToggleStatus = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/toggle-status`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchCustomers();
    } catch (e) { toast.error('Failed to update status'); }
    setActionLoading(null);
  };

  if (loading) return <AdminLayout title="Customers" subtitle="Manage customer accounts"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Customers" subtitle="Manage customer accounts">
      <div className="flex items-center justify-between mb-6">
        <Badge variant="secondary">{customers.length} total</Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-center">Email Verified</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone || '-'}</TableCell>
                  <TableCell className="text-center">
                    {customer.email_verified ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" /> Yes
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">
                        <XCircle className="h-3 w-3 mr-1" /> No
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={customer.is_active !== false ? 'bg-green-500' : 'bg-gray-500'}>
                      {customer.is_active !== false ? 'Active' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      {!customer.email_verified && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyEmail(customer.id)}
                            disabled={actionLoading === customer.id}
                            title="Manually verify email"
                          >
                            {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResendOTP(customer.id)}
                            disabled={actionLoading === customer.id}
                            title="Resend OTP"
                          >
                            {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant={customer.is_active !== false ? "destructive" : "default"}
                        onClick={() => handleToggleStatus(customer.id)}
                        disabled={actionLoading === customer.id}
                        title={customer.is_active !== false ? 'Disable user' : 'Enable user'}
                      >
                        {actionLoading === customer.id ? <Loader2 className="h-3 w-3 animate-spin" /> :
                          customer.is_active !== false ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />
                        }
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {customers.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">No customers found</div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
export const AdminProducts = () => {
  const { user, getAuthHeaders, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOption, setSortOption] = useState('newest');
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false, is_weekly_offer: false, is_test: false,
    cost_price: '', reorder_level: '', supplier_name: '', supplier_code: '',
    highlights: '', specifications: '', box_contents: '', faqs: '',
    content_toggles: { highlights: true, specifications: true, box_contents: true, faqs: true }
  });
  const [productToDelete, setProductToDelete] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;


  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchProducts = async () => {
    try { 
      const res = await axios.get(`${API}/products/all?limit=5000`, { headers: getAuthHeaders() }); 
      setProducts(res.data.products); 
    } catch (e) { 
      toast.error('Failed to load products'); 
    }
    setLoading(false);
  };


  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/categories/all`, { headers: getAuthHeaders() }); setCategories(res.data); } catch (e) { console.error('Failed to load categories'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Parse highlights (one per line)
    const highlights = formData.highlights ? formData.highlights.split('\n').map(s => s.trim()).filter(Boolean) : [];

    // Parse specifications (key: value per line)
    const specifications = {};
    if (formData.specifications) {
      formData.specifications.split('\n').forEach(line => {
        const [key, ...valueParts] = line.split(':');
        if (key && valueParts.length) {
          specifications[key.trim()] = valueParts.join(':').trim();
        }
      });
    }

    // Parse box contents (one per line)
    const box_contents = formData.box_contents ? formData.box_contents.split('\n').map(s => s.trim()).filter(Boolean) : [];

    // Parse FAQs (Q: and A: format)
    const faqs = [];
    if (formData.faqs) {
      const faqBlocks = formData.faqs.split(/\n\n+/);
      faqBlocks.forEach(block => {
        const qMatch = block.match(/Q:\s*(.+)/);
        const aMatch = block.match(/A:\s*(.+)/s);
        if (qMatch && aMatch) {
          faqs.push({ q: qMatch[1].trim(), a: aMatch[1].trim() });
        }
      });
    }

    const data = {
      ...formData,
      price: parseFloat(formData.price),
      offer_price: formData.offer_price ? parseFloat(formData.offer_price) : null,
      stock: parseInt(formData.stock) || 0,
      cost_price: parseFloat(formData.cost_price) || 0,
      reorder_level: parseInt(formData.reorder_level) || 10,
      images: formData.images ? formData.images.split(',').map(s => s.trim()).filter(Boolean) : [],
      highlights,
      specifications,
      box_contents,
      faqs,
      content_toggles: formData.content_toggles
    };

    // Remove string versions
    delete data.highlights;
    delete data.specifications;
    delete data.box_contents;
    delete data.faqs;
    data.highlights = highlights;
    data.specifications = specifications;
    data.box_contents = box_contents;
    data.faqs = faqs;

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, { headers: getAuthHeaders() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data, { headers: getAuthHeaders() });
        toast.success('Product created');
      }
      setShowForm(false); setEditingProduct(null); fetchProducts();
      setFormData({ name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false, is_weekly_offer: false, is_test: false, highlights: '', specifications: '', box_contents: '', faqs: '', content_toggles: { highlights: true, specifications: true, box_contents: true, faqs: true } });
    } catch (e) {
      console.error('Product save error:', e.response?.status, e.response?.data);
      if (e.response?.status === 401) {
        toast.error('Session expired — please log in again');
        navigate('/gpgt-portal');
      } else if (e.response?.status === 422) {
        // FastAPI validation error — show which field failed
        const errors = e.response?.data?.detail;
        if (Array.isArray(errors)) {
          const msg = errors.map(err => `${err.loc?.slice(-1)[0] || 'field'}: ${err.msg}`).join(', ');
          toast.error(`Validation error: ${msg}`);
        } else {
          toast.error(e.response?.data?.detail || 'Validation failed — check all required fields');
        }
      } else {
        toast.error(e.response?.data?.detail || 'Failed to save product');
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name, description: product.description, price: product.price?.toString(),
      offer_price: product.offer_price?.toString() || '', category_id: product.category_id,
      sku: product.sku, stock: product.stock?.toString(), brand: product.brand || '',
      cost_price: product.cost_price?.toString() || '', reorder_level: product.reorder_level?.toString() || '',
      supplier_name: product.supplier_name || '', supplier_code: product.supplier_code || '',
      images: product.images?.join(',') || '', is_active: product.is_active, is_featured: product.is_featured, is_weekly_offer: product.is_weekly_offer || false, is_test: product.is_test || false,
      highlights: product.highlights?.join('\n') || '',
      specifications: product.specifications ? Object.entries(product.specifications).map(([k, v]) => `${k}: ${v}`).join('\n') : '',
      box_contents: product.box_contents?.join('\n') || '',
      faqs: product.faqs ? product.faqs.map(f => `Q: ${f.q}\nA: ${f.a}`).join('\n\n') : '',
      content_toggles: product.content_toggles || { highlights: true, specifications: true, box_contents: true, faqs: true }
    });
    setShowForm(true);
  };

  useEffect(() => {
    if (products.length > 0 && location.search) {
      const params = new URLSearchParams(location.search);
      const editId = params.get('edit');
      if (editId) {
        const productToEdit = products.find(p => p.id === editId);
        if (productToEdit && !editingProduct) {
          handleEdit(productToEdit);
          navigate('/admin/products', { replace: true });
        }
      }
    }
  }, [products, location.search, navigate, editingProduct]);

  const handleDelete = async (id) => {
    setProductToDelete(id);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      await axios.delete(`${API}/products/${productToDelete}`, { headers: getAuthHeaders() });
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete product');
    }
    setProductToDelete(null);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    setBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const res = await axios.post(`${API}/products/bulk-delete`,
        { product_ids: [...selectedProducts] },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || 'Products deleted');
      setSelectedProducts(new Set());
      fetchProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    }
    setBulkDeleteConfirm(false);
  };

  const toggleSelect = (id) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredAndSortedProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredAndSortedProducts.map(p => p.id)));
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : '';
  };

  const filteredAndSortedProducts = useMemo(() => {
    let result = products.filter(p => {
      const matchesSearch = !searchQuery ||
        (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.sku || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.brand || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || p.category_id === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    // Apply sorting
    return [...result].sort((a, b) => {
      switch (sortOption) {
        case 'az':
          return (a.name || '').localeCompare(b.name || '');
        case 'za':
          return (b.name || '').localeCompare(a.name || '');
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return 0;
      }
    });
  }, [products, searchQuery, categoryFilter, sortOption]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, sortOption]);

  const totalPages = Math.ceil(filteredAndSortedProducts.length / productsPerPage);
  const paginatedProducts = filteredAndSortedProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );


  const [selectedProducts, setSelectedProducts] = useState(new Set());

  if (loading) return <AdminLayout title="Products" subtitle="Manage your product catalog"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" /></div></AdminLayout>;

  return (
    <AdminLayout title="Products" subtitle="Manage your product catalog">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Search by name, SKU, or brand..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-[#0c1b44]"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortOption} onValueChange={setSortOption}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Sort By" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">New to Old</SelectItem>
            <SelectItem value="oldest">Old to New</SelectItem>
            <SelectItem value="az">A to Z</SelectItem>
            <SelectItem value="za">Z to A</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="flex items-center gap-1 h-10 px-4 text-sm">
          <Package className="h-4 w-4" />{filteredAndSortedProducts.length} product{filteredAndSortedProducts.length !== 1 ? 's' : ''}
        </Badge>
        <Button onClick={() => { setEditingProduct(null); setFormData({ name: '', description: '', price: '', offer_price: '', category_id: '', sku: '', stock: '', brand: '', images: '', is_active: true, is_featured: false, is_weekly_offer: false, is_test: false, highlights: '', specifications: '', box_contents: '', faqs: '', content_toggles: { highlights: true, specifications: true, box_contents: true, faqs: true } }); setShowForm(true); }} className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"><Plus className="h-4 w-4 mr-2" />Add Product</Button>
      </div>

      {showForm ? (
        <Card className="mb-6">
          <CardHeader><CardTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="media">Images</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="seo">SEO & More</TabsTrigger>
                </TabsList>

                {/* Basic Info Tab */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Name *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>SKU *</Label><Input required value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} /></div>
                    <div className="space-y-2 md:col-span-2"><Label>Description *</Label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
                    <div className="space-y-2"><Label><span className="whitespace-nowrap">Price (AED) *</span></Label><Input type="number" step="0.01" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} /></div>
                    <div className="space-y-2"><Label><span className="whitespace-nowrap">Offer Price (AED)</span></Label><Input type="number" step="0.01" value={formData.offer_price} onChange={(e) => setFormData({ ...formData, offer_price: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Category *</Label>
                      <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-2"><Label>Stock *</Label><Input type="number" required value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Reorder Level</Label><Input type="number" value={formData.reorder_level} onChange={(e) => setFormData({ ...formData, reorder_level: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Cost Price</Label><Input type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Supplier Name</Label><Input value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Supplier Code</Label><Input value={formData.supplier_code} onChange={(e) => setFormData({ ...formData, supplier_code: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Brand</Label>
                      <Select value={formData.brand} onValueChange={(v) => setFormData({ ...formData, brand: v })}><SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger><SelectContent>{["Jaquar","Docol","OSRAM","MK","Ducab","Jotun","RAK Ceramics","GEZE","USG Boral","Hikvision","Granlace","DORMA"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Active</Label></div>
                      <div className="flex items-center gap-2"><Switch checked={formData.is_featured} onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })} /><Label>Featured</Label></div>
                      <div className="flex items-center gap-2"><Switch checked={formData.is_weekly_offer} onCheckedChange={(v) => setFormData({ ...formData, is_weekly_offer: v })} /><Label>Weekly Offer</Label></div>
                      <div className="flex items-center gap-2"><Switch checked={formData.is_test} onCheckedChange={(v) => setFormData({ ...formData, is_test: v })} /><Label>Test</Label></div>
                    </div>
                  </div>
                </TabsContent>

                {/* Images Tab - Enhanced Multiple Image Upload */}
                <TabsContent value="media" className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      <strong>Multiple Images Support:</strong> Upload multiple images at once. The first image will be used as the main product image. Drag to reorder images.
                    </p>
                  </div>

                  {/* Drag & Drop Zone */}
                  <div
                    className="border-2 border-dashed rounded-lg p-8 space-y-4 transition-colors hover:border-[#0c1b44] hover:bg-[#0c1b44]/5"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#0c1b44]', 'bg-[#0c1b44]/10'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-[#0c1b44]', 'bg-[#0c1b44]/10'); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-[#0c1b44]', 'bg-[#0c1b44]/10');
                      const files = e.dataTransfer.files;
                      if (!files || files.length === 0) return;
                      const uploadFormData = new FormData();
                      for (let i = 0; i < files.length; i++) {
                        if (files[i].type.startsWith('image/')) {
                          uploadFormData.append('files', files[i]);
                        }
                      }
                      try {
                        const res = await axios.post(`${API}/upload/images`, uploadFormData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                        const existingImages = formData.images ? formData.images.split(',').filter(Boolean) : [];
                        setFormData({ ...formData, images: [...existingImages, ...res.data.urls].join(',') });
                        toast.success(`${res.data.count} image(s) uploaded`);
                      } catch (err) { toast.error('Failed to upload images'); }
                    }}
                  >
                    <div className="text-center">
                      <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-lg font-medium text-navy mb-2">Drop images here or click to upload</p>
                      <p className="text-sm text-muted-foreground mb-4">Supports JPEG, PNG, WebP, GIF (Max 5MB each, up to 10 images)</p>

                      <input type="file" id="image-upload" accept="image/*" multiple className="hidden"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || files.length === 0) return;
                          const uploadFormData = new FormData();
                          for (let i = 0; i < files.length; i++) { uploadFormData.append('files', files[i]); }
                          try {
                            const res = await axios.post(`${API}/upload/images`, uploadFormData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                            const existingImages = formData.images ? formData.images.split(',').filter(Boolean) : [];
                            setFormData({ ...formData, images: [...existingImages, ...res.data.urls].join(',') });
                            toast.success(`${res.data.count} image(s) uploaded`);
                          } catch (err) { toast.error('Failed to upload images'); }
                          e.target.value = '';
                        }}
                      />
                      <Button type="button" onClick={() => document.getElementById('image-upload').click()} className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]">
                        <ImageIcon className="h-4 w-4 mr-2" /> Select Multiple Images
                      </Button>
                    </div>
                  </div>

                  {/* URL Input */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Or add by URL:</span>
                    <Input placeholder="Paste image URL and press Enter" className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const url = e.target.value.trim();
                          if (url) {
                            const existingImages = formData.images ? formData.images.split(',').filter(Boolean) : [];
                            setFormData({ ...formData, images: [...existingImages, url].join(',') });
                            e.target.value = '';
                            toast.success('Image URL added');
                          }
                        }
                      }}
                    />
                  </div>

                  {/* Image Gallery with Reorder */}
                  {formData.images && formData.images.split(',').filter(Boolean).length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-base font-semibold">Product Images ({formData.images.split(',').filter(Boolean).length})</Label>
                        <span className="text-xs text-muted-foreground">First image = Main image</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {formData.images.split(',').filter(Boolean).map((img, idx) => (
                          <div key={idx} className="relative group">
                            <div className={`aspect-square rounded-lg overflow-hidden border-2 bg-gray-50 ${idx === 0 ? 'border-[#0c1b44] ring-2 ring-gold/30' : 'border-gray-200'}`}>
                              <img
                                src={img.startsWith('/api') ? `${window.location.origin}${img}` : img}
                                alt={`Product ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            {idx === 0 && (
                              <span className="absolute top-1 left-1 bg-[#0c1b44] text-white hover:bg-[#1a2d64] text-xs px-2 py-0.5 rounded font-medium">
                                Main
                              </span>
                            )}
                            {/* Action buttons */}
                            <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Move Left */}
                              {idx > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const images = formData.images.split(',').filter(Boolean);
                                    [images[idx], images[idx - 1]] = [images[idx - 1], images[idx]];
                                    setFormData({ ...formData, images: images.join(',') });
                                  }}
                                  className="bg-gray-50 text-gray-700 rounded-full p-1 hover:bg-navy/80"
                                  title="Move left"
                                >
                                  <ChevronLeft className="h-3 w-3" />
                                </button>
                              )}
                              {/* Delete */}
                              <button
                                type="button"
                                onClick={() => {
                                  const images = formData.images.split(',').filter(Boolean);
                                  images.splice(idx, 1);
                                  setFormData({ ...formData, images: images.join(',') });
                                  toast.success('Image removed');
                                }}
                                className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                title="Remove image"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                            {/* Set as main button */}
                            {idx !== 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const images = formData.images.split(',').filter(Boolean);
                                  const [removed] = images.splice(idx, 1);
                                  images.unshift(removed);
                                  setFormData({ ...formData, images: images.join(',') });
                                  toast.success('Set as main image');
                                }}
                                className="absolute bottom-1 left-1 right-1 bg-navy/80 text-white text-xs py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-navy"
                              >
                                Set as Main
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Content Tab - Editable Product Page Content */}
                <TabsContent value="content" className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">Product Highlights</Label>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-normal text-muted-foreground">Show Section</Label>
                        <Switch 
                          checked={formData.content_toggles?.highlights !== false} 
                          onCheckedChange={(c) => setFormData({ ...formData, content_toggles: { ...formData.content_toggles, highlights: c } })} 
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">One highlight per line. These appear as bullet points on the product page.</p>
                    <Textarea rows={6} placeholder={"Premium quality materials\nEasy installation\nWater-efficient design\n1-year warranty included"} value={formData.highlights} onChange={(e) => setFormData({ ...formData, highlights: e.target.value })} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">Technical Specifications</Label>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-normal text-muted-foreground">Show Section</Label>
                        <Switch 
                          checked={formData.content_toggles?.specifications !== false} 
                          onCheckedChange={(c) => setFormData({ ...formData, content_toggles: { ...formData.content_toggles, specifications: c } })} 
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">Format: Key: Value (one per line)</p>
                    <Textarea rows={6} placeholder={"Material: Premium Grade Ceramic\nColor: White\nDimensions: 60 x 40 x 85 cm\nWeight: 25 kg"} value={formData.specifications} onChange={(e) => setFormData({ ...formData, specifications: e.target.value })} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">What's in the Box</Label>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-normal text-muted-foreground">Show Section</Label>
                        <Switch 
                          checked={formData.content_toggles?.box_contents !== false} 
                          onCheckedChange={(c) => setFormData({ ...formData, content_toggles: { ...formData.content_toggles, box_contents: c } })} 
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">One item per line</p>
                    <Textarea rows={4} placeholder={"Main product unit\nMounting hardware\nInstallation manual\nWarranty card"} value={formData.box_contents} onChange={(e) => setFormData({ ...formData, box_contents: e.target.value })} />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold">FAQs</Label>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-normal text-muted-foreground">Show Section</Label>
                        <Switch 
                          checked={formData.content_toggles?.faqs !== false} 
                          onCheckedChange={(c) => setFormData({ ...formData, content_toggles: { ...formData.content_toggles, faqs: c } })} 
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">Format: Q: Question (newline) A: Answer (separate each FAQ with blank line)</p>
                    <Textarea rows={8} placeholder={"Q: Is installation included?\nA: Installation service is available at additional cost.\n\nQ: What is the warranty period?\nA: This product comes with 1-year manufacturer warranty."} value={formData.faqs} onChange={(e) => setFormData({ ...formData, faqs: e.target.value })} />
                  </div>
                </TabsContent>

                {/* SEO Tab */}
                <TabsContent value="seo" className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">SEO meta tags are automatically generated from product name and description. Additional SEO fields coming soon.</p>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2 mt-6 pt-4 border-t">
                <Button type="submit" className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"><Save className="h-4 w-4 mr-2" />Save Product</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {filteredAndSortedProducts.length === 0 && !showForm ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="h-10 w-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-medium text-gray-500 mb-2">
            {products.length === 0 ? 'No products yet' : 'No matching products'}
          </h3>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            {products.length === 0 ? 'Click "Add Product" to add your first product.' : 'Try adjusting your search or category filter.'}
          </p>
        </div>
      ) : (
        <Card>
          {/* Bulk action bar */}
          {selectedProducts.size > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-red-50 border-b border-red-200 rounded-t-lg">
              <span className="text-sm font-medium text-red-700">
                {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedProducts(new Set())} className="h-8 text-xs">
                  Clear
                </Button>
                <Button size="sm" onClick={handleBulkDelete} className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Selected
                </Button>
              </div>
            </div>
          )}
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 cursor-pointer"
                  checked={filteredAndSortedProducts.length > 0 && selectedProducts.size === filteredAndSortedProducts.length}
                  onChange={toggleSelectAll}
                  title="Select all"
                />
              </TableHead>
              <TableHead>Image</TableHead><TableHead>Name</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {paginatedProducts.map((p) => (
                <TableRow key={p.id} className={`hover:bg-gray-50/50 ${selectedProducts.has(p.id) ? 'bg-blue-50/60' : ''}`}>
                  <TableCell>
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 cursor-pointer"
                      checked={selectedProducts.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                    />
                  </TableCell>
                  <TableCell><div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">{p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-full h-full p-2 text-muted-foreground" />}</div></TableCell>
                  <TableCell><div className="font-medium text-navy">{p.name}</div><div className="text-xs text-muted-foreground">{p.brand}</div></TableCell>
                  <TableCell><code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{p.sku}</code></TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{getCategoryName(p.category_id)}</Badge></TableCell>
                  <TableCell><div className="font-bold text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{p.offer_price || p.price}</span></div>{p.offer_price && p.offer_price < p.price && <div className="text-xs line-through text-muted-foreground"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{p.price}</span></div>}</TableCell>
                  <TableCell><Badge className={p.stock > 10 ? 'bg-emerald-100 text-emerald-700' : p.stock > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{p.stock}</Badge></TableCell>
                  <TableCell><Badge className={p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{p.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell><div className="flex gap-1 justify-end"><Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleEdit(p)}><Edit className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4" /></Button></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{(currentPage - 1) * productsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * productsPerPage, filteredAndSortedProducts.length)}</span> of <span className="font-medium">{filteredAndSortedProducts.length}</span> products
              </div>
              <div className="flex gap-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  // Show current, first, last, and pages around current
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className={`w-8 p-0 ${currentPage === page ? 'bg-[#0c1b44] text-white' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 py-1 text-gray-400">...</span>;
                  }
                  return null;
                })}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!productToDelete} onOpenChange={(open) => !open && setProductToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-gray-600">Are you sure you want to delete this product? This action cannot be undone.</div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setProductToDelete(null)}>Cancel</Button>
            <Button variant="destructive" className="bg-red-500 hover:bg-red-600 text-white" onClick={confirmDelete}>Delete Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Multiple Products</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-gray-600">Are you sure you want to delete {selectedProducts.size} selected product(s)? This action cannot be undone.</div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" className="bg-red-500 hover:bg-red-600 text-white" onClick={confirmBulkDelete}>Delete All Selected</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
export const AdminOrders = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const [autoOpenOrderId, setAutoOpenOrderId] = useState(searchParams.get('orderId'));
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const orderRowRefs = useRef({});

  // Quote editor state
  const [quoteMode, setQuoteMode] = useState(false);
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteShipping, setQuoteShipping] = useState(0);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteValidDays, setQuoteValidDays] = useState(7);
  const [sendingQuote, setSendingQuote] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Auto-open the order from the notification link once orders are loaded
  useEffect(() => {
    if (!autoOpenOrderId || orders.length === 0) return;
    const target = orders.find(o => String(o.id) === String(autoOpenOrderId) || o.order_number === autoOpenOrderId);
    if (target) {
      openOrder(target);
      setHighlightedOrderId(target.id);
      setTimeout(() => {
        const el = orderRowRefs.current[target.id];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      setAutoOpenOrderId(null);
    }
  }, [orders, autoOpenOrderId]);

  const fetchOrders = async () => {
    try { const res = await axios.get(`${API}/orders/all`, { headers: getAuthHeaders() }); setOrders(res.data.orders || []); } catch (e) { toast.error('Failed to load orders'); }
    setLoading(false);
  };

  const openOrder = async (o) => {
    setSelectedOrder(o);
    setQuoteMode(false);
    // Pre-fill quote items with current order items
    let base = (o.quoted_items?.length ? o.quoted_items : o.items) || [];
    
    try {
      const res = await axios.get(`${API}/products`);
      const allProducts = res.data.products || [];
      base = base.map(it => {
        const product = allProducts.find(p => p.id === it.product_id);
        const tag = (product?.is_weekly_offer && product?.specifications?._offer_tag) || null;
        
        let calcPrice = product?.price ? parseFloat(product.price) : parseFloat(it.unit_price);
        
        if (tag) {
          const match = tag.match(/(\d+(?:\.\d+)?)%/);
          if (match) {
            const percent = parseFloat(match[1]);
            calcPrice = calcPrice - (calcPrice * (percent / 100));
          }
        }

        return {
          ...it,
          _offer_tag: tag,
          _suggested_price: calcPrice
        };
      });
    } catch (e) {
      console.error('Could not load products for offer tags');
    }

    setQuoteItems(base.map(it => {
      // If the checkout price is an artificial placeholder (1 or 0), OR if we have a newly calculated discounted price, use the suggested price.
      const priceToUse = (parseFloat(it.unit_price) <= 1 || it._suggested_price !== undefined) ? (it._suggested_price || parseFloat(it.unit_price)) : parseFloat(it.unit_price);
      
      return {
        ...it,
        unit_price: parseFloat(priceToUse.toFixed(2)) || 0,
        quantity: parseInt(it.quantity) || 1,
        total_price: parseFloat((priceToUse * (parseInt(it.quantity) || 1)).toFixed(2)),
      };
    }));
    setQuoteShipping(parseFloat(o.quoted_shipping) || 0);
    setQuoteNotes(o.quote_admin_notes || '');
    setQuoteValidDays(7);
  };

  const updateQuoteItem = (idx, field, val) => {
    setQuoteItems(prev => {
      const arr = [...prev];
      const it = { ...arr[idx] };
      if (field === 'quantity') it.quantity = parseInt(val) || 1;
      else it[field] = parseFloat(val) || 0;
      it.total_price = parseFloat((it.unit_price * it.quantity).toFixed(2));
      arr[idx] = it;
      return arr;
    });
  };

  const qSubtotal = quoteItems.reduce((s, i) => s + (i.total_price || 0), 0);
  const qVat = parseFloat((qSubtotal * 0.05).toFixed(2));
  const qTotal = parseFloat((qSubtotal + qVat + (parseFloat(quoteShipping) || 0)).toFixed(2));

  const sendQuote = async () => {
    if (!selectedOrder) return;
    setSendingQuote(true);
    try {
      const res = await axios.post(`${API}/orders/${selectedOrder.id}/send-quote`, {
        items: quoteItems,
        shipping: parseFloat(quoteShipping) || 0,
        admin_notes: quoteNotes || null,
        valid_days: quoteValidDays,
      }, { headers: getAuthHeaders() });
      toast.success(`✅ Quote sent! Total: AED ${res.data.quoted_total}`);
      if (!res.data.email_sent) toast.info('Note: SMTP not configured — email not sent. Configure SMTP in .env to enable.');
      setSelectedOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send quote');
    }
    setSendingQuote(false);
  };

  const [shippingDialog, setShippingDialog] = useState(null); // { orderId, status }
  const [trackingNumber, setTrackingNumber] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [sendingShipping, setSendingShipping] = useState(false);

  const SHIPPING_STATUSES = ['shipped', 'out_for_delivery', 'delivered'];

  const updateStatus = async (orderId, status) => {
    // Shipping statuses → open dialog to collect tracking + note
    if (SHIPPING_STATUSES.includes(status)) {
      setShippingDialog({ orderId, status });
      setTrackingNumber('');
      setDeliveryNote('');
      return;
    }
    // Other statuses → update immediately
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success('Status updated');
      fetchOrders();
    } catch (e) { toast.error('Failed to update'); }
  };

  const confirmShippingUpdate = async () => {
    if (!shippingDialog) return;
    setSendingShipping(true);
    try {
      const params = new URLSearchParams({ status: shippingDialog.status });
      if (trackingNumber) params.append('tracking_number', trackingNumber);
      if (deliveryNote) params.append('note', deliveryNote);
      await axios.put(`${API}/orders/${shippingDialog.orderId}/status?${params}`, {}, { headers: getAuthHeaders() });
      toast.success(`✅ Order marked as ${shippingDialog.status.replace('_', ' ')} — Customer notified by email!`);
      setShippingDialog(null);
      fetchOrders();
    } catch (e) { toast.error('Failed to update status'); }
    setSendingShipping(false);
  };

  const updatePaymentStatus = async (orderId, status) => {
    try { await axios.put(`${API}/orders/${orderId}/payment-status?status=${status}`, {}, { headers: getAuthHeaders() }); toast.success('Payment status updated'); fetchOrders(); } catch (e) { toast.error('Failed to update payment status'); }
  };

  // Confirm order (set payment=paid + status=confirmed) — triggers auto invoice
  const confirmOrder = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/payment-status?status=paid`, {}, { headers: getAuthHeaders() });
      await axios.put(`${API}/orders/${orderId}/status?status=confirmed`, {}, { headers: getAuthHeaders() });
      toast.success('✅ Order confirmed! Invoice will be emailed to customer automatically.');
      fetchOrders();
    } catch (e) { toast.error('Failed to confirm order'); }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/invoice`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${res.data.content}`;
      link.download = res.data.filename;
      link.click();
    } catch (e) { toast.error('Failed to download invoice'); }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-blue-100 text-blue-700',
    processing: 'bg-purple-100 text-purple-700',
    shipped: 'bg-indigo-100 text-indigo-700',
    out_for_delivery: 'bg-orange-100 text-orange-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  const statuses = ['all', 'pending', 'confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled'];
  const getStatusCount = (s) => s === 'all' ? orders.length : orders.filter(o => o.status === s).length;

  const filteredOrders = orders.filter(o => {
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    const matchesSearch = !searchQuery ||
      (o.order_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.shipping_address?.full_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (o.shipping_address?.phone || '').includes(searchQuery);
    return matchesStatus && matchesSearch;
  });

  if (loading) return <AdminLayout title="Orders" subtitle="Track and manage customer orders"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" /></div></AdminLayout>;

  const statusLabel = (s) => s === 'out_for_delivery' ? 'Out for Delivery' : s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <AdminLayout title="Orders" subtitle="Track and manage customer orders">

      {/* ── Shipping Update Dialog ─────────────────────────────────────── */}
      {shippingDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className={`px-6 py-5 flex items-center gap-3 ${
              shippingDialog.status === 'shipped' ? 'bg-indigo-600' :
              shippingDialog.status === 'out_for_delivery' ? 'bg-orange-500' : 'bg-emerald-600'
            }`}>
              <span className="text-2xl">
                {shippingDialog.status === 'shipped' ? '📦' : shippingDialog.status === 'out_for_delivery' ? '🛵' : '✅'}
              </span>
              <div>
                <h3 className="text-white font-bold text-lg">
                  Mark as {statusLabel(shippingDialog.status)}
                </h3>
                <p className="text-white/70 text-sm">Customer will receive an email notification</p>
              </div>
            </div>
            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#0c1b44]">
                  Tracking Number <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={e => setTrackingNumber(e.target.value)}
                  placeholder="e.g. YT2604100703612782"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0c1b44]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#0c1b44]">
                  Note to Customer <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <textarea
                  value={deliveryNote}
                  onChange={e => setDeliveryNote(e.target.value)}
                  placeholder="e.g. Your package will be delivered between 10am–2pm."
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#0c1b44]"
                />
              </div>
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                <span>📧</span>
                <span>A branded email notification will be sent to the customer automatically when you confirm.</span>
              </div>
            </div>
            {/* Footer */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShippingDialog(null)}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmShippingUpdate}
                disabled={sendingShipping}
                className={`flex-1 rounded-lg py-2.5 text-sm font-bold text-white flex items-center justify-center gap-2 ${
                  shippingDialog.status === 'shipped' ? 'bg-indigo-600 hover:bg-indigo-700' :
                  shippingDialog.status === 'out_for_delivery' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {sendingShipping ? <Loader2 className="h-4 w-4 animate-spin" /> : '📧'}
                {sendingShipping ? 'Sending...' : `Confirm & Notify Customer`}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {statuses.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${statusFilter === s ? 'bg-[#0c1b44] text-white shadow' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
            {s === 'all' ? 'All' : statusLabel(s)}
            <span className={`ml-1.5 text-xs ${statusFilter === s ? 'text-white/70' : 'text-muted-foreground'}`}>{getStatusCount(s)}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search by order number, customer name, or phone..."
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b44]/20" />
      </div>

      {filteredOrders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <ClipboardList className="h-8 w-8 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium">No orders found</p>
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead>
                <TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((o) => (
                <TableRow
                  key={o.id}
                  ref={el => orderRowRefs.current[o.id] = el}
                  className={`hover:bg-gray-50/50 transition-colors duration-500 ${highlightedOrderId === o.id ? 'bg-blue-50/80 border-l-4 border-blue-500' : ''}`}
                >
                  <TableCell>
                    <div className="font-medium text-navy">{o.order_number}</div>
                    <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {o.payment_method === 'quotation' && <span className="text-[11px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">RFQ</span>}
                      {o.quote_status === 'quoted' && <span className="text-[11px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Quote Sent</span>}
                      {o.quote_status === 'accepted' && <span className="text-[11px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Accepted</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{o.shipping_address?.full_name}</div>
                    <div className="text-xs text-muted-foreground">{o.shipping_address?.phone}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{o.items?.length} items</Badge></TableCell>
                  <TableCell>
                    <div className="font-bold text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.total?.toFixed(2)}</span></div>
                    {o.quoted_total && <div className="text-xs text-amber-600 font-semibold">Quoted: <span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.quoted_total?.toFixed(2)}</span></div>}
                  </TableCell>
                  <TableCell>
                    <Select value={o.payment_status || 'pending'} onValueChange={(v) => updatePaymentStatus(o.id, v)}>
                      <SelectTrigger className={`w-28 h-8 text-xs font-medium ${o.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : o.payment_status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['pending', 'paid', 'failed', 'refunded'].map(s => <SelectItem key={s} value={s}>{s === 'pending' && o.payment_method === 'cod' ? 'COD (Pending)' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {/* Prominent Send Quote button for quotation-type pending orders */}
                      {o.payment_method === 'quotation' && o.status === 'pending' && o.quote_status !== 'accepted' && (
                        <Dialog open={selectedOrder?.id === o.id && quoteMode} onOpenChange={(isOpen) => !isOpen && setSelectedOrder(null)}>
                          <DialogTrigger asChild>
                            <Button size="sm"
                              className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold gap-1"
                              onClick={() => { openOrder(o); setQuoteMode(true); }}
                              title="Send Price Quote"
                            >
                              <Send className="h-3 w-3" />
                              {o.quote_status === 'quoted' ? 'Re-Quote' : 'Send Quote'}
                            </Button>
                          </DialogTrigger>
                        </Dialog>
                      )}

                      {/* Confirm Order button — for accepted quotes awaiting admin confirmation */}
                      {o.quote_status === 'accepted' && o.status === 'pending' && o.payment_status !== 'paid' && (
                        <Button size="sm"
                          className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold gap-1"
                          onClick={() => confirmOrder(o.id)}
                          title="Mark as Paid & Confirm Order"
                        >
                          <CheckCircle className="h-3 w-3" /> Confirm
                        </Button>
                      )}
                    <div className="flex gap-1 justify-end">
                      {/* Order Detail + Quote Dialog */}
                      <Dialog open={selectedOrder?.id === o.id} onOpenChange={(isOpen) => !isOpen && setSelectedOrder(null)}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openOrder(o)} title="View / Quote">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-3">
                              Order {o.order_number}
                              <Badge className={statusColors[o.status] || 'bg-gray-100'}>{o.status}</Badge>
                              {o.quote_status === 'quoted' && <Badge className="bg-amber-100 text-amber-700">Quote Sent</Badge>}
                              {o.quote_status === 'accepted' && <Badge className="bg-green-100 text-green-700">Quote Accepted</Badge>}
                            </DialogTitle>
                          </DialogHeader>

                          {/* Tab switcher */}
                          <div className="flex gap-1 border-b mb-4">
                            <button onClick={() => setQuoteMode(false)}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${!quoteMode ? 'border-[#0c1b44] text-[#0c1b44]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                              📋 Order Details
                            </button>
                            <button onClick={() => setQuoteMode(true)}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${quoteMode ? 'border-amber-500 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                              💰 Edit Prices & Send Quote
                            </button>
                          </div>

                          {!quoteMode ? (
                            /* ── ORDER DETAILS VIEW ── */
                            <div className="space-y-4">
                              {/* Confirm Order Banner — shown when quote accepted, awaiting admin confirmation */}
                              {o.quote_status === 'accepted' && o.payment_status !== 'paid' && (
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-4">
                                  <div>
                                    <p className="font-semibold text-green-800">✅ Customer Accepted the Quote!</p>
                                    <p className="text-sm text-green-700 mt-0.5">Quoted Total: <strong>AED {o.quoted_total?.toFixed(2)}</strong>. Confirm once payment is received to auto-generate the invoice.</p>
                                  </div>
                                  <Button
                                    className="bg-green-600 hover:bg-green-700 text-white font-semibold gap-2 shrink-0"
                                    onClick={() => { confirmOrder(o.id); setSelectedOrder(null); }}
                                  >
                                    <CheckCircle className="h-4 w-4" /> Confirm Order
                                  </Button>
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-4">
                                <Card className="bg-gray-50 border-0"><CardContent className="p-4">
                                  <h4 className="font-semibold text-sm text-navy mb-2">Shipping Address</h4>
                                  <p className="text-sm">{o.shipping_address?.full_name}<br />{o.shipping_address?.address_line1}<br />{o.shipping_address?.city}, {o.shipping_address?.emirate}<br />{o.shipping_address?.phone}</p>
                                </CardContent></Card>
                                <Card className="bg-gray-50 border-0"><CardContent className="p-4">
                                  <h4 className="font-semibold text-sm text-navy mb-2">Order Info</h4>
                                  <div className="text-sm space-y-1">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span>{o.payment_method?.toUpperCase()}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Payment Status</span><Badge className={o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{o.payment_status}</Badge></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{new Date(o.created_at).toLocaleDateString()}</span></div>
                                    {o.customer_email && <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="text-xs truncate max-w-[140px]">{o.customer_email}</span></div>}
                                  </div>
                                </CardContent></Card>
                              </div>
                              <div>
                                <h4 className="font-semibold text-sm text-navy mb-2">Items</h4>
                                <div className="border rounded-lg overflow-hidden">
                                  {o.items?.map((item, i) => (
                                    <div key={i} className="flex justify-between items-center px-4 py-3 border-b last:border-0 bg-white">
                                      <div>
                                        <span className="font-medium">
                                          {item.product_name}
                                          {quoteItems[i]?._offer_tag && (
                                            <Badge variant="outline" className="ml-2 bg-red-50 text-red-600 border-red-200">
                                              {quoteItems[i]._offer_tag}
                                            </Badge>
                                          )}
                                        </span>
                                        <span className="text-muted-foreground ml-2">×{item.quantity}</span>
                                      </div>
                                      <span className="font-medium"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{item.total_price?.toFixed(2)}</span></span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <Card className="bg-gray-50 border-0"><CardContent className="p-4">
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between text-gray-600"><span>Subtotal</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.subtotal?.toFixed(2)}</span></span></div>
                                  <div className="flex justify-between text-gray-600"><span>VAT (5%)</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.vat_amount?.toFixed(2)}</span></span></div>
                                  <div className="flex justify-between text-gray-600"><span>Shipping</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.shipping_cost?.toFixed(2)}</span></span></div>
                                  <Separator className="my-2" />
                                  <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.total?.toFixed(2)}</span></span></div>
                                </div>
                                {o.quoted_total && (
                                  <div className="mt-3 pt-3 border-t">
                                    <div className="flex justify-between text-amber-700 font-semibold">
                                      <span>🏷️ Quoted Price</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{o.quoted_total?.toFixed(2)}</span></span>
                                    </div>
                                    {o.quote_valid_until && <p className="text-xs text-muted-foreground mt-1">Valid until {o.quote_valid_until}</p>}
                                  </div>
                                )}
                              </CardContent></Card>
                              <div className="flex justify-end">
                                <Button variant="outline" size="sm" onClick={() => setQuoteMode(true)} className="gap-2 text-amber-700 border-amber-300 hover:bg-amber-50">
                                  <Send className="h-4 w-4" /> Edit Prices & Send Quote
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* ── QUOTE EDITOR VIEW ── */
                            <div className="space-y-4">
                              {/* Customer info banner */}
                              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm flex items-start gap-3">
                                <User className="h-4 w-4 text-[#0c1b44] mt-0.5 flex-shrink-0" />
                                <div>
                                  <div className="font-semibold text-[#0c1b44]">{o.shipping_address?.full_name}</div>
                                  <div className="text-xs text-gray-500">{o.customer_email} · {o.shipping_address?.phone}</div>
                                  <div className="text-xs text-gray-500">{o.shipping_address?.city}, {o.shipping_address?.emirate}</div>
                                </div>
                              </div>

                              {/* Editable price table */}
                              <div>
                                <h4 className="font-semibold text-sm text-[#0c1b44] mb-2 flex items-center gap-2">
                                  <ClipboardList className="h-4 w-4" /> Edit Item Prices
                                  <span className="text-xs font-normal text-muted-foreground">(change any price below)</span>
                                </h4>
                                <div className="border rounded-lg overflow-hidden text-sm">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-[#0c1b44] text-white text-xs">
                                        <th className="px-3 py-2.5 text-left">Product</th>
                                        <th className="px-2 py-2.5 text-center w-14">Qty</th>
                                        <th className="px-3 py-2.5 text-right w-36">Unit Price (<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>)</th>
                                        <th className="px-3 py-2.5 text-right w-28">Line Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {quoteItems.map((item, idx) => (
                                        <tr key={idx} className={`border-b last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}>
                                          <td className="px-3 py-2.5 font-medium">
                                            {item.product_name}
                                            {item._offer_tag && (
                                              <Badge variant="outline" className="ml-2 bg-red-50 text-red-600 border-red-200">
                                                {item._offer_tag}
                                              </Badge>
                                            )}
                                          </td>
                                          <td className="px-2 py-2.5 text-center">
                                            <input type="number" min="1" value={item.quantity}
                                              onChange={e => updateQuoteItem(idx, 'quantity', e.target.value)}
                                              className="w-12 text-center border rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44]" />
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <div className="flex items-center justify-end gap-1">
                                              <span className="text-xs text-muted-foreground"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg></span>
                                              <input type="number" min="0" step="0.01" value={item.unit_price}
                                                onChange={e => updateQuoteItem(idx, 'unit_price', e.target.value)}
                                                className="w-24 text-right border rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0c1b44] bg-white" />
                                            </div>
                                          </td>
                                          <td className="px-3 py-2.5 text-right font-bold text-[#0c1b44]">
                                            {(item.total_price || 0).toFixed(2)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Quote totals calculator */}
                              <div className="bg-[#0c1b44]/5 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between text-gray-600"><span>Subtotal</span><strong><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{qSubtotal.toFixed(2)}</span></strong></div>
                                <div className="flex justify-between text-gray-600"><span>VAT (5%)</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{qVat.toFixed(2)}</span></span></div>
                                <div className="flex justify-between text-gray-600 items-center">
                                  <span>Shipping</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg></span>
                                    <input type="number" min="0" step="0.01" value={quoteShipping}
                                      onChange={e => setQuoteShipping(e.target.value)}
                                      className="w-20 text-right border rounded px-2 py-0.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1b44]" />
                                  </div>
                                </div>
                                <Separator />
                                <div className="flex justify-between font-bold text-base pt-1">
                                  <span className="text-[#0c1b44]">Total Quoted Price</span>
                                  <span className="text-amber-600 text-lg"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{qTotal.toFixed(2)}</span></span>
                                </div>
                              </div>

                              {/* Quote settings */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-semibold text-gray-600 block mb-1">Quote Valid For (days)</label>
                                  <input type="number" min="1" max="90" value={quoteValidDays}
                                    onChange={e => setQuoteValidDays(parseInt(e.target.value) || 7)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44]" />
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-600 block mb-1">Send To</label>
                                  <div className="text-sm border rounded-lg px-3 py-2 bg-gray-50 text-gray-600 truncate">
                                    {o.customer_email || o.shipping_address?.email || '(no email on record)'}
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Note to Customer (optional)</label>
                                <textarea rows={2} value={quoteNotes} onChange={e => setQuoteNotes(e.target.value)}
                                  placeholder="e.g. Special pricing applied. Valid for the specified period only."
                                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44] resize-none" />
                              </div>

                              {/* Send Button */}
                              <div className="flex items-center justify-between gap-3 pt-2 border-t">
                                <p className="text-xs text-muted-foreground">
                                  Quote will be emailed to customer &amp; saved to their dashboard
                                </p>
                                <Button onClick={sendQuote} disabled={sendingQuote}
                                  className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white gap-2 px-6 shrink-0">
                                  {sendingQuote
                                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                                    : <><Send className="h-4 w-4" /> Send Quote to Customer</>}
                                </Button>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => downloadInvoice(o.id)} title="Download Invoice"><Download className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </AdminLayout>
  );
};
export const AdminCategories = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_ar: '', description: '', image: '', icon: '', parent_id: '', is_active: true });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/categories/all`, { headers: getAuthHeaders() }); setCategories(res.data); } catch (e) { toast.error('Failed to load categories'); }
    setLoading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, uploadFormData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image: res.data.url });
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, formData, { headers: getAuthHeaders() });
        toast.success('Category created');
      }
      setShowForm(false); setEditingCategory(null); fetchCategories();
      setFormData({ name: '', name_ar: '', description: '', image: '', icon: '', parent_id: '', is_active: true });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save category'); }
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat);
    setFormData({ name: cat.name, name_ar: cat.name_ar || '', description: cat.description || '', image: cat.image || '', icon: cat.icon || '', parent_id: cat.parent_id || '', is_active: cat.is_active });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeaders() }); toast.success('Category deleted'); fetchCategories(); } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <AdminLayout title="Categories" subtitle="Organize your product catalog"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Categories" subtitle="Organize your product catalog">
      <div className="flex items-center justify-end mb-6">
        <Button onClick={() => { setEditingCategory(null); setFormData({ name: '', name_ar: '', description: '', image: '', icon: '', parent_id: '', is_active: true }); setShowForm(true); }} className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"><Plus className="h-4 w-4 mr-2" />Add Category</Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name (English) *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Name (Arabic)</Label><Input value={formData.name_ar} onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })} dir="rtl" /></div>
              <div className="space-y-2 md:col-span-2">
                <Label>Parent Category (Optional)</Label>
                <Select value={formData.parent_id} onValueChange={(v) => setFormData({ ...formData, parent_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select Parent Category (for sub-categories)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Parent (Main Category) --</SelectItem>
                    {categories.filter(c => c.id !== editingCategory?.id).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>

              <div className="space-y-2 md:col-span-2">
                <Label>Category Image</Label>
                <div className="flex flex-col md:flex-row gap-4 items-start">
                  <div className="w-full md:w-32 h-32 bg-muted rounded-lg overflow-hidden border">
                    {formData.image ? (
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-4 w-full">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Image URL"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        className="flex-1"
                      />
                      <input
                        type="file"
                        id="cat-image-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('cat-image-upload').click()}
                        disabled={uploading}
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                        Upload
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Upload an image or paste a URL. Supports JPG, PNG, WebP.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2"><Label>Icon (Lucide icon name)</Label><Input value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="e.g. Droplets, Zap" /></div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Active</Label></div>
              <div className="md:col-span-2 flex gap-2"><Button type="submit" className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]">Save</Button><Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => (
          <Card key={cat.id} className="overflow-hidden">
            <div className="h-32 bg-muted">{cat.image ? <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>}</div>
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold">{cat.name}</h3>
                  {cat.name_ar && <p className="text-sm text-[#0c1b44]" dir="rtl">{cat.name_ar}</p>}
                  {cat.parent_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Sub-category of: {categories.find(c => c.id === cat.parent_id)?.name || 'Unknown'}
                    </p>
                  )}
                </div>
                <Badge className={cat.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{cat.is_active ? 'Active' : 'Inactive'}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{cat.product_count || 0} products</p>
              <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(cat)}><Edit className="h-4 w-4 mr-1" />Edit</Button><Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(cat.id)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button></div>
            </CardContent>
          </Card>
        ))}
      </div>
    </AdminLayout>
  );
};

// Admin Settings
export const AdminSettings = () => {
  const { user, getAuthHeaders, fetchSettings: refreshAppSettings } = useApp();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroSlides, setHeroSlides] = useState([]);
  const [savingHero, setSavingHero] = useState(false);
  const [footerGallery, setFooterGallery] = useState(Array(6).fill(''));
  const [savingGallery, setSavingGallery] = useState(false);
  const [uploadingGalleryIdx, setUploadingGalleryIdx] = useState(null);
  const [promoBanners, setPromoBanners] = useState([]);
  const [savingPromo, setSavingPromo] = useState(false);
  const [uploadingPromoIdx, setUploadingPromoIdx] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/admin`, { headers: getAuthHeaders() });
      setSettings(res.data);
      setHeroSlides(res.data.hero_slides || []);
      const existing = Array.isArray(res.data.footer_gallery) ? res.data.footer_gallery : [];
      const padded = Array.from({ length: 6 }, (_, i) => existing[i] || '');
      setFooterGallery(padded);
      // Fetch promo banners from dedicated endpoint
      try {
        const pbRes = await axios.get(`${API}/promo-banners`);
        setPromoBanners(pbRes.data.banners || []);
      } catch (e) { console.warn('Could not fetch promo banners'); }
    } catch (e) { toast.error('Failed to load settings'); }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() });
      toast.success('Settings saved successfully');
      await refreshAppSettings(); // reflect show_prices changes immediately
    } catch (e) { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  if (loading) return <AdminLayout title="Store Settings" subtitle="Configure your store preferences"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Store Settings" subtitle="Configure your store preferences">

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Payment Gateway Toggle - Prominent Card */}
        <Card className={`lg:col-span-2 border-2 ${settings.payment_enabled ? 'border-green-500 bg-green-50/30' : 'border-orange-500 bg-orange-50/30'}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#0c1b44]" />
              Payment Gateway
              <Badge className={settings.payment_enabled ? 'bg-green-500' : 'bg-orange-500'}>
                {settings.payment_enabled ? 'ENABLED' : 'DISABLED'}
              </Badge>
            </CardTitle>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white self-end ml-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
              Save All Settings
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
              <div>
                <Label className="text-lg font-semibold">Online Payment (Stripe)</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {settings.payment_enabled
                    ? 'Customers can pay with credit/debit cards at checkout'
                    : 'Online payment is OFF. Orders will be sent to admin email for manual processing'}
                </p>
              </div>
              <Switch
                checked={settings.payment_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, payment_enabled: v })}
                className="scale-125"
              />
            </div>

            {!settings.payment_enabled && (
              <div className="bg-orange-100 border border-orange-300 rounded-lg p-4">
                <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Payment Gateway Disabled Mode
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  When customers complete checkout, their order details will be emailed to: <strong>{settings.admin_notification_email || 'sales@gpgt.ae'}</strong>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Admin Notification Email</Label>
              <Input
                type="email"
                placeholder="admin@example.com"
                value={settings.admin_notification_email || ''}
                onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Order notifications will be sent to this email when payment is disabled</p>
            </div>

            <Separator />

            <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm">
              <div>
                <Label className="text-base font-medium">Cash on Delivery (COD)</Label>
                <p className="text-sm text-muted-foreground">Allow customers to pay cash upon delivery</p>
              </div>
              <Switch checked={settings.cod_enabled} onCheckedChange={(v) => setSettings({ ...settings, cod_enabled: v })} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Percent className="h-5 w-5 text-[#0c1b44]" />Tax & Shipping</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>VAT Percentage (%)</Label><Input type="number" step="0.1" value={settings.vat_percentage || ''} onChange={(e) => setSettings({ ...settings, vat_percentage: parseFloat(e.target.value) })} /></div>
            <div className="space-y-2"><Label>Flat Shipping Rate (<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>)</Label><Input type="number" step="0.01" value={settings.flat_shipping_rate || ''} onChange={(e) => setSettings({ ...settings, flat_shipping_rate: parseFloat(e.target.value) })} /></div>
            <div className="space-y-2"><Label>Free Shipping Threshold (<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>)</Label><Input type="number" step="0.01" value={settings.free_shipping_threshold || ''} onChange={(e) => setSettings({ ...settings, free_shipping_threshold: parseFloat(e.target.value) })} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Building className="h-5 w-5 text-[#0c1b44]" />Company Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Company Name</Label><Input value={settings.company_name || ''} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>TRN (Tax Registration Number)</Label><Input value={settings.company_trn || ''} onChange={(e) => setSettings({ ...settings, company_trn: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={settings.company_phone || ''} onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={settings.company_email || ''} onChange={(e) => setSettings({ ...settings, company_email: e.target.value })} /></div>
            <div className="space-y-2"><Label>WhatsApp Number</Label><Input value={settings.whatsapp_number || ''} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} /></div>
            <div className="space-y-2"><Label>Address</Label><Textarea value={settings.company_address || ''} onChange={(e) => setSettings({ ...settings, company_address: e.target.value })} /></div>
          </CardContent>
        </Card>
      </div>

      {/* Site Logos Management */}
      <Card className="mt-6 border-2 border-navy/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-[#0c1b44]" />
            Site Logos
          </CardTitle>
          <p className="text-sm text-muted-foreground">Manage the main header logo and the footer logo used across the website.</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Header Logo */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Header Logo</Label>
              <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center relative group">
                {settings.header_logo ? (
                  <>
                    <img src={settings.header_logo.startsWith('/api') ? `${window.location.origin}${settings.header_logo}` : settings.header_logo} alt="Header Logo" className="w-auto h-24 object-contain" />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-sm"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSettings(prev => ({ ...prev, header_logo: "" }));
                        try {
                          await axios.put(`${API}/settings`, { header_logo: "" }, { headers: getAuthHeaders() });
                          toast.success('Header logo removed');
                        } catch (err) { toast.error('Failed to remove logo'); }
                      }}
                      title="Remove Header Logo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground"><ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No logo uploaded</p></div>
                )}
                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer text-white z-10">
                  <Upload className="h-6 w-6 mb-1" />
                  <span className="text-sm font-medium">{settings.header_logo ? 'Replace Logo' : 'Upload Logo'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const uploadData = new FormData(); uploadData.append('file', file);
                    try {
                      const res = await axios.post(`${API}/upload/image`, uploadData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                      const newUrl = res.data.url;
                      setSettings(prev => ({ ...prev, header_logo: newUrl }));
                      await axios.put(`${API}/settings`, { header_logo: newUrl }, { headers: getAuthHeaders() });
                      toast.success('Header logo updated');
                    } catch (err) { toast.error('Failed to update logo'); }
                    e.target.value = '';
                  }} />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Recommended size: 200x80px (PNG with transparent background)</p>
            </div>

            {/* Footer Logo */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Footer Logo</Label>
              <div className="w-full h-32 rounded-lg overflow-hidden bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center relative group">
                {settings.footer_logo ? (
                  <>
                    <img src={settings.footer_logo.startsWith('/api') ? `${window.location.origin}${settings.footer_logo}` : settings.footer_logo} alt="Footer Logo" className="w-auto h-20 object-contain mix-blend-multiply" />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-sm"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSettings(prev => ({ ...prev, footer_logo: "" }));
                        try {
                          await axios.put(`${API}/settings`, { footer_logo: "" }, { headers: getAuthHeaders() });
                          toast.success('Footer logo removed');
                        } catch (err) { toast.error('Failed to remove logo'); }
                      }}
                      title="Remove Footer Logo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground"><ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No logo uploaded</p></div>
                )}
                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer text-white z-10">
                  <Upload className="h-6 w-6 mb-1" />
                  <span className="text-sm font-medium">{settings.footer_logo ? 'Replace Logo' : 'Upload Logo'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const uploadData = new FormData(); uploadData.append('file', file);
                    try {
                      const res = await axios.post(`${API}/upload/image`, uploadData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                      const newUrl = res.data.url;
                      setSettings(prev => ({ ...prev, footer_logo: newUrl }));
                      await axios.put(`${API}/settings`, { footer_logo: newUrl }, { headers: getAuthHeaders() });
                      toast.success('Footer logo updated');
                    } catch (err) { toast.error('Failed to update logo'); }
                    e.target.value = '';
                  }} />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Recommended size: 200x80px (PNG or JPG)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hero Banners Management */}
      <Card className="mt-6 border-2 border-navy/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-[#0c1b44]" />
            Hero Banners
            <Badge variant="outline" className="ml-2">{heroSlides.length} slides</Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">Manage the homepage hero carousel images. Each slide has an image, title, Arabic title, and subtitle.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {heroSlides.map((slide, idx) => (
            <Card key={idx} className="bg-gray-50 border">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Image preview */}
                  <div className="w-40 h-24 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 relative group">
                    {slide.image ? (
                      <img src={slide.image.startsWith('/api') ? `${window.location.origin}${slide.image}` : slide.image} alt={slide.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground"><ImageIcon className="h-8 w-8" /></div>
                    )}
                    {/* Upload overlay */}
                    <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                      <Upload className="h-6 w-6 text-white" />
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const uploadData = new FormData();
                        uploadData.append('file', file);
                        try {
                          const res = await axios.post(`${API}/upload/image`, uploadData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                          const updated = [...heroSlides];
                          updated[idx] = { ...updated[idx], image: res.data.url };
                          setHeroSlides(updated);
                          toast.success('Image uploaded');
                        } catch (err) { toast.error('Failed to upload image'); }
                        e.target.value = '';
                      }} />
                    </label>
                  </div>

                  {/* Fields */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Upload button replacing URL input */}
                    <div className="space-y-1">
                      <Label className="text-xs">Slide Image</Label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-white hover:bg-gray-50 text-sm font-medium text-[#0c1b44] transition-colors whitespace-nowrap">
                          <Upload className="h-4 w-4" />
                          {slide.image ? 'Change Image' : 'Upload Image'}
                        </span>
                        <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                          {slide.image
                            ? (slide.image.split('/').pop()?.slice(0, 20) || 'Uploaded')
                            : 'No file chosen'}
                        </span>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const uploadData = new FormData();
                          uploadData.append('file', file);
                          try {
                            const res = await axios.post(`${API}/upload/image`, uploadData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                            const updated = [...heroSlides];
                            updated[idx] = { ...updated[idx], image: res.data.url };
                            setHeroSlides(updated);
                            toast.success('Image uploaded');
                          } catch (err) { toast.error('Failed to upload image'); }
                          e.target.value = '';
                        }} />
                      </label>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input placeholder="Slide title" value={slide.title || ''} onChange={(e) => { const updated = [...heroSlides]; updated[idx] = { ...updated[idx], title: e.target.value }; setHeroSlides(updated); }} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Arabic Title</Label>
                      <Input placeholder="عنوان" dir="rtl" value={slide.title_ar || ''} onChange={(e) => { const updated = [...heroSlides]; updated[idx] = { ...updated[idx], title_ar: e.target.value }; setHeroSlides(updated); }} />
                    </div>
                    <div className="sm:col-span-3 space-y-1">
                      <Label className="text-xs">Subtitle</Label>
                      <Input placeholder="Subtitle text" value={slide.subtitle || ''} onChange={(e) => { const updated = [...heroSlides]; updated[idx] = { ...updated[idx], subtitle: e.target.value }; setHeroSlides(updated); }} />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    {idx > 0 && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { const updated = [...heroSlides];[updated[idx], updated[idx - 1]] = [updated[idx - 1], updated[idx]]; setHeroSlides(updated); }} title="Move up"><ChevronUp className="h-4 w-4" /></Button>}
                    {idx < heroSlides.length - 1 && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { const updated = [...heroSlides];[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]; setHeroSlides(updated); }} title="Move down"><ChevronDown className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => { const updated = heroSlides.filter((_, i) => i !== idx); setHeroSlides(updated); }} title="Remove slide"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" onClick={() => setHeroSlides([...heroSlides, { image: '', title: '', title_ar: '', subtitle: '' }])} className="border-dashed">
              <Plus className="h-4 w-4 mr-2" />Add Slide
            </Button>
            <Button type="button" onClick={async () => {
              setSavingHero(true);
              try {
                await axios.put(`${API}/hero-slides`, { slides: heroSlides }, { headers: getAuthHeaders() });
                toast.success('Hero banners saved!');
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save hero banners'); }
              setSavingHero(false);
            }} disabled={savingHero} className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]">
              {savingHero ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save Hero Banners
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Footer Gallery Section */}
      <Card className="mt-6 border-2 border-navy/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-[#0c1b44]" />
            Scan to Connect — QR Gallery
          </CardTitle>
          <p className="text-sm text-muted-foreground">Upload QR code images for each platform (3 columns × 2 rows) shown in the footer. Each slot links directly to the social media page when clicked.</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {['1. Facebook','2. Instagram','3. LinkedIn','4. X (Twitter)','5. YouTube','6. TikTok'].map((label) => (
              <span key={label} className="text-xs bg-[#0c1b44]/10 text-[#0c1b44] rounded px-2 py-0.5 font-medium">{label}</span>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3 mb-4 max-w-xs">
            {footerGallery.map((src, i) => (
              <div key={i} className="relative group aspect-square">
                <div className="w-full h-full rounded-lg overflow-hidden bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                  {src ? (
                    <img src={src.startsWith('/api') ? `${window.location.origin}${src}` : src} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-gray-300" />
                  )}
                </div>
                {/* Upload overlay */}
                <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer rounded-lg">
                  {uploadingGalleryIdx === i ? (
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-white" />
                      <span className="text-white text-[10px] mt-1">{i + 1}</span>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingGalleryIdx(i);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                      const updated = [...footerGallery];
                      updated[i] = res.data.url;
                      setFooterGallery(updated);
                      toast.success(`Image ${i + 1} uploaded`);
                    } catch (err) { toast.error('Upload failed'); }
                    setUploadingGalleryIdx(null);
                    e.target.value = '';
                  }} />
                </label>
                {/* Remove button */}
                {src && (
                  <button
                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full h-4 w-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => { const updated = [...footerGallery]; updated[i] = ''; setFooterGallery(updated); }}
                  >×</button>
                )}
              </div>
            ))}
          </div>
          <Button
            onClick={async () => {
              setSavingGallery(true);
              try {
                const clean = footerGallery.filter(Boolean);
                await axios.put(`${API}/settings`, { footer_gallery: clean }, { headers: getAuthHeaders() });
                await refreshAppSettings();
                toast.success('Footer gallery saved!');
              } catch (err) { toast.error('Failed to save gallery'); }
              setSavingGallery(false);
            }}
            disabled={savingGallery}
            className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"
          >
            {savingGallery ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Gallery
          </Button>
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-end"><Button onClick={handleSave} disabled={saving} className="bg-[#0c1b44] text-white font-bold px-8">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save Settings</Button></div>
    </AdminLayout>
  );
};

// ==================== FEATURED PRODUCTS MANAGEMENT ====================
export const AdminFeaturedProducts = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalSearch, setModalSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products?limit=500`, { headers: getAuthHeaders() });
      setProducts(res.data.products || []);
    } catch (e) { toast.error('Failed to load products'); }
    setLoading(false);
  };

  const toggleFeatured = async (product, isAdding) => {
    setTogglingId(product.id);
    try {
      await axios.put(`${API}/products/${product.id}`, { is_featured: isAdding }, { headers: getAuthHeaders() });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_featured: isAdding } : p));
      toast.success(isAdding ? `"${product.name}" added to featured` : `"${product.name}" removed from featured`);
      if (isAdding) setShowAddModal(false);
    } catch (e) { toast.error('Failed to update'); }
    setTogglingId(null);
  };

  const uploadFeaturedImage = async (productId, file) => {
    setUploadingId(productId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/image`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      const imageUrl = uploadRes.data.url;
      const product = products.find(p => p.id === productId);
      const updatedImages = [imageUrl, ...(product.images || []).filter(img => img !== imageUrl)];
      await axios.put(`${API}/products/${productId}`, { images: updatedImages }, { headers: getAuthHeaders() });
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, images: updatedImages } : p));
      toast.success('Featured image uploaded');
    } catch (e) { toast.error('Failed to upload image'); }
    setUploadingId(null);
  };

  // Only show featured products in the main table
  const featuredProducts = products.filter(p => p.is_featured);
  const filteredFeatured = featuredProducts.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  // Unfeatured products for the Add modal
  const unfeaturedProducts = products.filter(p => !p.is_featured);
  const modalFiltered = unfeaturedProducts.filter(p =>
    !modalSearch || p.name.toLowerCase().includes(modalSearch.toLowerCase()) || (p.sku || '').toLowerCase().includes(modalSearch.toLowerCase())
  );

  if (loading) return <AdminLayout title="Featured Products"><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" /></div></AdminLayout>;

  return (
    <AdminLayout title="Featured Products" subtitle="Manage highlighted products on the homepage">
      <div className="flex justify-between items-center mb-6">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search featured products..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"><Plus className="h-4 w-4 mr-2" /> Add Featured Product</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Add to Featured Products</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search catalog by name or SKU..." className="pl-9" value={modalSearch} onChange={(e) => setModalSearch(e.target.value)} />
              </div>
              <div className="border rounded-lg overflow-hidden h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-24 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modalFiltered.length === 0 ? (
                      <TableRow><TableCell colSpan={2} className="text-center py-8 text-muted-foreground">No matching products found in catalog</TableCell></TableRow>
                    ) : (
                      modalFiltered.slice(0, 50).map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                                <img src={p.images?.[0] || 'https://via.placeholder.com/40'} alt={p.name} className="w-full h-full object-cover" />
                              </div>
                              <div>
                                <p className="font-medium text-sm leading-tight">{p.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{p.sku || 'No SKU'} • <span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{p.price?.toFixed(2)}</span></p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" onClick={() => toggleFeatured(p, true)} disabled={togglingId === p.id} className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-8 px-4">
                              {togglingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3 mr-1" />Add</>}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Featured Products Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16 text-center">Image</TableHead>
                <TableHead>Product Details</TableHead>
                <TableHead className="w-28 text-center">Featured Image</TableHead>
                <TableHead className="w-20 text-center">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFeatured.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-16">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed">
                      <Star className="h-8 w-8 text-gray-300" />
                    </div>
                    <p className="font-semibold text-lg text-navy mb-1">No Featured Products</p>
                    <p className="text-muted-foreground text-sm mb-4">Click "Add Featured Product" to highlight items on your homepage.</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFeatured.map((p) => (
                  <TableRow key={p.id} className="hover:bg-gray-50/50">
                    <TableCell>
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 mx-auto border shadow-sm">
                        <img src={p.images?.[0] || 'https://via.placeholder.com/56'} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-navy leading-tight mb-1">{p.name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-semibold text-[#0c1b44]"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{p.price?.toFixed(2)}</span></span>
                          <span>•</span>
                          <span>SKU: {p.sku || 'N/A'}</span>
                          <span>•</span>
                          <span>Stock: {p.stock}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <label className="inline-flex items-center justify-center cursor-pointer group">
                        <div className="flex flex-col items-center justify-center w-24 h-14 border border-dashed rounded bg-gray-50 hover:bg-[#0c1b44]/5 hover:border-[#0c1b44] transition-colors">
                          {uploadingId === p.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <>
                              <Upload className="h-4 w-4 text-muted-foreground group-hover:text-[#0c1b44] mb-1" />
                              <span className="text-[10px] text-muted-foreground group-hover:text-[#0c1b44] font-medium">Upload Hero</span>
                            </>
                          )}
                        </div>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadFeaturedImage(p.id, file);
                          e.target.value = '';
                        }} />
                      </label>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleFeatured(p, false)}
                        disabled={togglingId === p.id}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50"
                        title="Remove from featured"
                      >
                        {togglingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

// ==================== ADMIN QUOTATIONS ====================
export const AdminQuotations = () => {
  const { user, getAuthHeaders } = useApp();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [searchParams] = useSearchParams();
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [adminNotes, setAdminNotes] = useState('');
  const [validDays, setValidDays] = useState(7);
  const [shipping, setShipping] = useState(0);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [highlightedEnqId, setHighlightedEnqId] = useState(null);
  const [autoOpenEnqId, setAutoOpenEnqId] = useState(searchParams.get('enquiryId'));
  const enqCardRefs = useRef({});

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Auto-open and scroll to specific enquiry if linked directly 
  useEffect(() => {
    if (!autoOpenEnqId || enquiries.length === 0) return;
    const target = enquiries.find(e => String(e.id) === String(autoOpenEnqId) || e.enquiry_number === autoOpenEnqId);
    if (target) {
      openQuoteEditor(target);
      setHighlightedEnqId(target.id);
      setTimeout(() => {
        const el = enqCardRefs.current[target.id];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      setAutoOpenEnqId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enquiries, autoOpenEnqId]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cart-enquiries`, { headers: getAuthHeaders() });
      setEnquiries(res.data.enquiries || []);
    } catch (e) { toast.error('Failed to load quotation requests'); }
    setLoading(false);
  };

  const openQuoteEditor = (enq) => {
    setSelectedEnquiry(enq);
    setOpenDialog(enq.id);
    const baseItems = (enq.quoted_items?.length ? enq.quoted_items : enq.items) || [];
    setQuoteItems(baseItems.map(item => ({
      ...item,
      unit_price: parseFloat(item.unit_price) || 0,
      total_price: parseFloat(item.total_price) || 0,
    })));
    setAdminNotes(enq.admin_notes || '');
    setValidDays(7);
    setShipping(parseFloat(enq.quoted_shipping) || 0);
  };

  const updateItemPrice = (idx, field, val) => {
    setQuoteItems(prev => {
      const updated = [...prev];
      const item = { ...updated[idx] };
      if (field === 'quantity') {
        item.quantity = parseInt(val) || 1;
      } else if (field === 'unit_price') {
        item.unit_price = parseFloat(val) || 0;
      }
      item.total_price = parseFloat((item.unit_price * item.quantity).toFixed(2));
      updated[idx] = item;
      return updated;
    });
  };

  const subtotal = quoteItems.reduce((s, i) => s + (i.total_price || 0), 0);
  const vatPct = 5;
  const vat = parseFloat((subtotal * vatPct / 100).toFixed(2));
  const total = parseFloat((subtotal + vat + (parseFloat(shipping) || 0)).toFixed(2));

  const sendQuote = async () => {
    if (!selectedEnquiry) return;
    setSending(true);
    try {
      const payload = {
        items: quoteItems,
        subtotal,
        vat,
        shipping: parseFloat(shipping) || 0,
        total,
        admin_notes: adminNotes || null,
        valid_days: validDays,
      };
      const res = await axios.post(
        `${API}/cart-enquiries/${selectedEnquiry.id}/send-quote`,
        payload,
        { headers: getAuthHeaders() }
      );
      toast.success(`✅ Quotation sent to ${selectedEnquiry.customer?.email}!`);
      if (!res.data.email_sent) {
        toast.info('SMTP not configured — status saved but no email sent. Add SMTP credentials to .env to enable emails.');
      }
      setOpenDialog(null);
      setSelectedEnquiry(null);
      fetchEnquiries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send quotation');
    }
    setSending(false);
  };

  const updateStatus = async (enqId, status) => {
    setUpdatingStatus(enqId + status);
    try {
      await axios.put(`${API}/cart-enquiries/${enqId}?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success(`Status updated to ${status}`);
      fetchEnquiries();
    } catch (e) { toast.error('Failed to update status'); }
    setUpdatingStatus(null);
  };

  const statusConfig = {
    new:      { label: 'New',      bg: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-500' },
    quoted:   { label: 'Quoted',   bg: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    accepted: { label: 'Accepted', bg: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
    rejected: { label: 'Rejected', bg: 'bg-red-100 text-red-700',     dot: 'bg-red-500' },
    closed:   { label: 'Closed',   bg: 'bg-gray-100 text-gray-600',   dot: 'bg-gray-400' },
  };

  const statuses = ['all', 'new', 'quoted', 'accepted', 'rejected'];
  const filtered = enquiries.filter(e => {
    const matchStatus = statusFilter === 'all' || e.status === statusFilter;
    const q = searchQ.toLowerCase();
    const matchSearch = !q ||
      (e.enquiry_number || '').toLowerCase().includes(q) ||
      (e.customer?.name || '').toLowerCase().includes(q) ||
      (e.customer?.email || '').toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  if (loading) return (
    <AdminLayout title="Quotations" subtitle="Manage and send price quotations to customers">
      <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0c1b44]" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Quotations" subtitle="Review enquiries, set custom prices, and send professional quotes">

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {['all','new','quoted','accepted'].map(s => {
          const cnt = s === 'all' ? enquiries.length : enquiries.filter(e => e.status === s).length;
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                statusFilter === s ? 'border-[#0c1b44] bg-[#0c1b44]/5 shadow-sm' : 'border-transparent bg-white shadow-sm hover:border-gray-200'
              }`}>
              <p className="text-2xl font-bold text-[#0c1b44]">{cnt}</p>
              <p className="text-sm font-medium text-gray-600 mt-0.5 capitalize">
                {s === 'all' ? 'Total Enquiries' : (statusConfig[s]?.label || s)}
              </p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text" placeholder="Search by name, email or reference..."
            value={searchQ} onChange={e => setSearchQ(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b44]/20"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {statuses.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                statusFilter === s ? 'bg-[#0c1b44] text-white shadow' : 'bg-white border text-gray-600 hover:bg-gray-50'
              }`}>
              {s === 'all' ? 'All' : (statusConfig[s]?.label || s)}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={fetchEnquiries} className="gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Enquiries */}
      {filtered.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-xl border">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="h-10 w-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-500 mb-1">No quotation requests yet</h3>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            When customers submit quote requests from the store, they'll appear here for you to price and send.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(enq => {
            const cfg = statusConfig[enq.status] || statusConfig.new;
            const isOpen = openDialog === enq.id;
            return (
              <Card 
                key={enq.id} 
                ref={el => enqCardRefs.current[enq.id] = el}
                className={`overflow-hidden transition-all duration-500 hover:shadow-md ${highlightedEnqId === enq.id ? 'bg-blue-50/50 border-blue-400 shadow-md ring-2 ring-blue-500/20' : ''}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Left info */}
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#0c1b44] text-base">{enq.enquiry_number}</span>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.bg}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                              {cfg.label}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Received {new Date(enq.created_at).toLocaleDateString('en-AE', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                          </div>
                        </div>
                        {enq.valid_until && enq.status === 'quoted' && (
                          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded">
                            Valid until {enq.valid_until}
                          </div>
                        )}
                      </div>

                      {/* Customer */}
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-3">
                        <div className="w-9 h-9 bg-[#0c1b44]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-[#0c1b44]" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm">{enq.customer?.name}</div>
                          <div className="text-xs text-muted-foreground">{enq.customer?.email} · {enq.customer?.phone}</div>
                          {enq.shipping_address && (
                            <div className="text-xs text-muted-foreground">{enq.shipping_address.city}, {enq.shipping_address.emirate}</div>
                          )}
                        </div>
                      </div>

                      {/* Items summary */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Requested Items</p>
                        {(enq.items || []).map((item, i) => (
                          <div key={i} className="flex justify-between items-center text-sm py-1 border-b border-gray-100 last:border-0">
                            <span className="text-gray-700">{item.product_name}</span>
                            <span className="text-gray-500 text-xs">× {item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right pricing panel */}
                    <div className="lg:w-64 bg-gray-50 border-t lg:border-t-0 lg:border-l p-5 flex flex-col gap-3">
                      {/* Price summary */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pricing</p>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between text-gray-600">
                            <span>Customer's estimate</span>
                            <span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{(enq.total || 0).toFixed(2)}</span></span>
                          </div>
                          {enq.quoted_total != null && (
                            <div className="flex justify-between font-bold text-[#0c1b44]">
                              <span>Your quoted price</span>
                              <span className="text-amber-600"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{enq.quoted_total.toFixed(2)}</span></span>
                            </div>
                          )}
                        </div>
                        {enq.admin_notes && (
                          <div className="mt-2 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2 italic">
                            "{enq.admin_notes}"
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="space-y-2 mt-auto">
                        <Dialog open={isOpen} onOpenChange={open => { setOpenDialog(open ? enq.id : null); }}>
                          <DialogTrigger asChild>
                            <Button
                              className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white gap-2 text-sm"
                              onClick={() => openQuoteEditor(enq)}>
                              <DollarSign className="h-4 w-4" />
                              {enq.status === 'quoted' ? 'Edit & Resend Quote' : 'Set Price & Send Quote'}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2 text-[#0c1b44]">
                                <FileText className="h-5 w-5" />
                                Quotation Builder — {enq.enquiry_number}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-5 mt-1">
                              {/* Customer info */}
                              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm flex items-start gap-3">
                                <div className="w-9 h-9 bg-[#0c1b44]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <User className="h-4 w-4 text-[#0c1b44]" />
                                </div>
                                <div>
                                  <div className="font-semibold text-[#0c1b44]">{enq.customer?.name}</div>
                                  <div className="text-xs text-gray-500">{enq.customer?.email} · {enq.customer?.phone}</div>
                                  <div className="text-xs text-gray-500">{enq.shipping_address?.city}, {enq.shipping_address?.emirate}</div>
                                </div>
                              </div>

                              {/* Price editor table */}
                              <div>
                                <h4 className="font-semibold text-sm text-[#0c1b44] mb-2 flex items-center gap-2">
                                  <ClipboardList className="h-4 w-4" /> Edit Item Prices
                                  <span className="text-xs font-normal text-muted-foreground">(default prices shown — edit to set your quote price)</span>
                                </h4>
                                <div className="border rounded-lg overflow-hidden text-sm">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="bg-[#0c1b44] text-white text-xs">
                                        <th className="px-3 py-2.5 text-left">Product</th>
                                        <th className="px-2 py-2.5 text-center w-14">Qty</th>
                                        <th className="px-3 py-2.5 text-right w-36">Unit Price (<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>)</th>
                                        <th className="px-3 py-2.5 text-right w-28">Line Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {quoteItems.map((item, idx) => (
                                        <tr key={idx} className={`border-b last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}>
                                          <td className="px-3 py-2.5">
                                            <div className="font-medium leading-tight">{item.product_name}</div>
                                          </td>
                                          <td className="px-2 py-2.5 text-center">
                                            <input
                                              type="number" min="1"
                                              value={item.quantity}
                                              onChange={e => updateItemPrice(idx, 'quantity', e.target.value)}
                                              className="w-12 text-center border rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44]"
                                            />
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <div className="flex items-center justify-end gap-1">
                                              <span className="text-xs text-muted-foreground"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg></span>
                                              <input
                                                type="number" min="0" step="0.01"
                                                value={item.unit_price}
                                                onChange={e => updateItemPrice(idx, 'unit_price', e.target.value)}
                                                className="w-24 text-right border rounded-lg px-2 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0c1b44] focus:border-[#0c1b44] bg-white"
                                              />
                                            </div>
                                          </td>
                                          <td className="px-3 py-2.5 text-right font-bold text-[#0c1b44]">
                                            {item.total_price.toFixed(2)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Totals calculator */}
                              <div className="bg-[#0c1b44]/5 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between text-gray-600">
                                  <span>Subtotal</span><strong><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{subtotal.toFixed(2)}</span></strong>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>VAT (5%)</span><span><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{vat.toFixed(2)}</span></span>
                                </div>
                                <div className="flex justify-between text-gray-600 items-center">
                                  <span>Shipping</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" style={{verticalAlign:"middle",position:"relative",top:"-0.08em",marginRight:"4px"}}><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg></span>
                                    <input
                                      type="number" min="0" step="0.01"
                                      value={shipping}
                                      onChange={e => setShipping(e.target.value)}
                                      className="w-20 text-right border rounded px-2 py-0.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1b44]"
                                    />
                                  </div>
                                </div>
                                <Separator />
                                <div className="flex justify-between font-bold text-base pt-1">
                                  <span className="text-[#0c1b44]">Total Quoted Price</span>
                                  <span className="text-amber-600 text-lg"><span className="whitespace-nowrap inline-flex items-center"><svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1.15em" height="1.15em" className="mr-1 flex-shrink-0"><path d="M3 3h7.5C13.8 3 17 6.2 17 9.5S13.8 16 10.5 16H3V3zm2.5 2.5V13.5H10.5c1.93 0 4-1.57 4-4S12.43 5.5 10.5 5.5H5.5z"/><rect x="1" y="8" width="16" height="1.8" rx="0.5"/><rect x="1" y="11.5" width="16" height="1.8" rx="0.5"/></svg>{total.toFixed(2)}</span></span>
                                </div>
                              </div>

                              {/* Quote settings */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-semibold text-gray-600 block mb-1">Quote Valid For (days)</label>
                                  <input
                                    type="number" min="1" max="90"
                                    value={validDays}
                                    onChange={e => setValidDays(parseInt(e.target.value) || 7)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44]"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-600 block mb-1">Customer Email</label>
                                  <div className="text-sm border rounded-lg px-3 py-2 bg-gray-50 text-gray-600 truncate">
                                    {enq.customer?.email}
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="text-xs font-semibold text-gray-600 block mb-1">Note to Customer (optional)</label>
                                <textarea
                                  rows={2}
                                  value={adminNotes}
                                  onChange={e => setAdminNotes(e.target.value)}
                                  placeholder="e.g. Bulk discount applied. Prices are valid for the specified period only."
                                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0c1b44] resize-none"
                                />
                              </div>

                              {/* Action row */}
                              <div className="flex items-center justify-between gap-3 pt-2 border-t">
                                <p className="text-xs text-muted-foreground">
                                  A branded quote PDF will be emailed to <strong>{enq.customer?.email}</strong>
                                </p>
                                <Button
                                  onClick={sendQuote}
                                  disabled={sending}
                                  className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white gap-2 px-6 shrink-0"
                                >
                                  {sending
                                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                                    : <><Send className="h-4 w-4" /> Send Quotation</>
                                  }
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>

                        {/* Status quick actions */}
                        <div className="flex gap-2">
                          {enq.status !== 'accepted' && (
                            <Button size="sm" variant="outline"
                              className="flex-1 text-xs text-green-600 border-green-300 hover:bg-green-50 gap-1"
                              disabled={updatingStatus === enq.id + 'accepted'}
                              onClick={() => updateStatus(enq.id, 'accepted')}>
                              <CheckCircle className="h-3.5 w-3.5" /> Accepted
                            </Button>
                          )}
                          {enq.status !== 'rejected' && (
                            <Button size="sm" variant="outline"
                              className="flex-1 text-xs text-red-500 border-red-200 hover:bg-red-50 gap-1"
                              disabled={updatingStatus === enq.id + 'rejected'}
                              onClick={() => updateStatus(enq.id, 'rejected')}>
                              <XCircle className="h-3.5 w-3.5" /> Rejected
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};
